import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

/**
 * Reusable Multi-Select Dropdown Component
 *
 * @param {Array} options - Array of objects with { id, title, subtitle? }
 * @param {Array} selectedIds - Array of selected IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {String} placeholder - Placeholder text when nothing selected
 * @param {Number} maxSelection - Maximum number of items that can be selected (default: 25)
 * @param {String} searchPlaceholder - Placeholder for search input
 * @param {Boolean} showSelectAll - Show "Select All" option (default: true)
 * @param {Boolean} disabled - Disable the dropdown
 * @param {Function} renderOption - Custom render function for options (optional)
 */
const MultiSelectDropdown = ({
  options = [],
  selectedIds = [],
  onSelectionChange,
  placeholder = 'Select items...',
  maxSelection = 25,
  searchPlaceholder = 'Search...',
  showSelectAll = true,
  disabled = false,
  renderOption = null,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectAll, setSelectAll] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Sync selectAll state with selectedIds
  useEffect(() => {
    if (options.length > 0 && selectedIds.length === options.length) {
      setSelectAll(true)
    } else {
      setSelectAll(false)
    }
  }, [selectedIds, options])

  // Handle individual item toggle
  const handleItemToggle = (id) => {
    if (selectedIds.includes(id)) {
      // Remove item
      onSelectionChange(selectedIds.filter((itemId) => itemId !== id))
    } else {
      // Add item (check max limit)
      if (selectedIds.length >= maxSelection) {
        toast.error(`You can select up to ${maxSelection} items at once`)
        return
      }
      onSelectionChange([...selectedIds, id])
    }
  }

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (selectAll) {
      // Deselect all
      onSelectionChange([])
      setSelectAll(false)
    } else {
      // Select all (check max limit)
      if (options.length > maxSelection) {
        toast.error(`Cannot select all - maximum ${maxSelection} items allowed`)
        return
      }
      onSelectionChange(options.map((option) => option.id))
      setSelectAll(true)
    }
  }

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Default render function
  const defaultRenderOption = (option) => (
    <span className="text-sm text-gray-700">{option.title}</span>
  )

  const renderOptionFn = renderOption || defaultRenderOption

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <div
        className={`w-full border border-gray-300 rounded-lg p-3 cursor-pointer bg-white transition-colors flex justify-between items-center ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-gray-50'
            : 'hover:border-emerald-500'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          {selectedIds.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {/* Show first 3 selected items as pills */}
              {selectedIds.slice(0, 3).map((id) => {
                const item = options.find((opt) => opt.id === id)
                return item ? (
                  <span
                    key={id}
                    className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {item.title}
                  </span>
                ) : null
              })}
              {/* Show "+N more" if more than 3 selected */}
              {selectedIds.length > 3 && (
                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium">
                  +{selectedIds.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
        {/* Dropdown Arrow Icon */}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Select All Option */}
          {showSelectAll && options.length > 0 && (
            <div
              className="flex items-center px-3 py-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                handleSelectAllToggle()
              }}
            >
              <input
                type="checkbox"
                checked={selectAll}
                onChange={() => {}}
                className="mr-3 h-4 w-4 accent-emerald-500 cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">
                Select All
              </span>
            </div>
          )}

          {/* Items List */}
          <div className="max-h-44 overflow-y-auto">
            {options.length === 0 ? (
              <p className="text-sm text-gray-500 italic p-3">
                No items available
              </p>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center px-3 py-2 hover:bg-emerald-50 cursor-pointer transition-colors ${
                    selectedIds.includes(item.id) ? 'bg-emerald-50' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleItemToggle(item.id)
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => {}}
                    className="mr-3 h-4 w-4 accent-emerald-500 cursor-pointer"
                  />
                  {renderOptionFn(item)}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic p-3">
                No items found matching "{searchTerm}"
              </p>
            )}
          </div>
        </div>
      )}

      {/* Selected Count */}
      {selectedIds.length > 0 && (
        <p className="text-xs text-emerald-600 mt-2 font-medium">
          {selectedIds.length} / {maxSelection} item(s) selected
        </p>
      )}
    </div>
  )
}

export default MultiSelectDropdown
